var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import DatePicker from '../../../../../Elements/DatePicker';
import Input from '../../../../../Forms/Input';
import { useClickOutside } from '../../../../Modal/hooks/useClickOutside';
import { getCleanValues } from './utils/getCleanValues';
import { pickerRangeToString } from './utils/pickerRangeToString';
import { i18n } from '@lingui/core';
import styled from '@xstyled/styled-components';
var RangeWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: inline-flex;\n  margin-right: 20px;\n"], ["\n  display: inline-flex;\n  margin-right: 20px;\n"])));
var OverlayComponentDefault = function (_a) {
    var children = _a.children;
    return _jsx("div", { children: children });
};
var DatePickerRange = function (props) {
    var _a, _b, _c;
    var onChange = props.onChange, error = props.error, label = props.label, value = props.value, others = props.others, filterTime = props.filterTime, initialView = props.initialView, _d = props.OverlayComponent, OverlayComponent = _d === void 0 ? OverlayComponentDefault : _d, rest = __rest(props, ["onChange", "error", "label", "value", "others", "filterTime", "initialView", "OverlayComponent"]);
    var _e = useState((_a = value === null || value === void 0 ? void 0 : value[0]) !== null && _a !== void 0 ? _a : undefined), startRangeValue = _e[0], setStartRangeValue = _e[1];
    var _f = useState((_b = value === null || value === void 0 ? void 0 : value[1]) !== null && _b !== void 0 ? _b : undefined), endRangeValue = _f[0], setEndRangeValue = _f[1];
    var timeFormat = filterTime ? 'HH:mm' : false;
    var placeholder = i18n._({ id: 'dataGrid.filterCell', message: 'Type to filter' });
    var cleanValue = React.useMemo(function () { return getCleanValues(value); }, [value]);
    var initialViewDate = useMemo(function () {
        if (cleanValue[0] && cleanValue[1]) {
            return cleanValue;
        }
        var firstDate = new Date();
        firstDate.setHours(0, 0, 0, 0);
        var secondDate = new Date();
        if (initialView === 'future') {
            secondDate.setMonth(secondDate.getMonth() + 1);
        }
        else {
            firstDate.setMonth(firstDate.getMonth());
        }
        return [cleanValue[0] || firstDate, cleanValue[1] || secondDate];
    }, [cleanValue, initialView]);
    var _g = useState(false), isOpen = _g[0], setOpen = _g[1];
    var setDateFrom = useCallback(function (v) {
        setStartRangeValue(v);
        onChange([v, cleanValue[1]], others);
    }, [onChange, others, cleanValue]);
    var setDateTo = useCallback(function (v) {
        setEndRangeValue(v);
        onChange([cleanValue[0], v], others);
    }, [onChange, others, cleanValue]);
    var handleOthersChangeFrom = useCallback(function (othersFrom) {
        onChange(cleanValue, [othersFrom, others === null || others === void 0 ? void 0 : others[1]]);
    }, [onChange, cleanValue, others]);
    var handleOthersChangeTo = useCallback(function (othersTo) {
        onChange(cleanValue, [others === null || others === void 0 ? void 0 : others[0], othersTo]);
    }, [onChange, cleanValue, others]);
    var openPicker = useCallback(function () {
        setOpen(true);
    }, [setOpen]);
    var clear = useCallback(function () {
        onChange([], []);
        setStartRangeValue(undefined);
        setEndRangeValue(undefined);
    }, [onChange]);
    var isValidDateFrom = useCallback(function (current) { return !cleanValue[1] || DateTime.fromJSDate(cleanValue[1]) >= DateTime.fromJSDate(current); }, [cleanValue]);
    var isValidDateTo = useCallback(function (current) { return !cleanValue[0] || DateTime.fromJSDate(current) >= DateTime.fromJSDate(cleanValue[0]); }, [cleanValue]);
    var onClose = useCallback(function () {
        setOpen(false);
    }, []);
    // Check if the second value is not selected and the first value is the same as the initial value
    var secondDatePickerValue = cleanValue[1] === null ? cleanValue[0] : cleanValue[1];
    var ref = useClickOutside({ onClose: onClose });
    return (_jsxs("div", { ref: ref, children: [_jsx(Input, { name: props.name, placeholder: placeholder, type: "text", icon: "calendar", value: pickerRangeToString(cleanValue, others), label: props.label, onFocus: openPicker, iconOnClick: openPicker, onClear: clear, error: error, readOnly: true }), _jsx(OverlayComponent, { children: _jsx(_Fragment, { children: isOpen && (_jsxs(RangeWrapper, { className: "ignore-element-for-closing-hook", children: [_jsx(DatePicker, __assign({}, rest, { timeFormat: timeFormat, value: (_c = cleanValue[0]) !== null && _c !== void 0 ? _c : '', initialViewDate: initialViewDate[0], secondValue: endRangeValue, onChange: setDateFrom, others: others === null || others === void 0 ? void 0 : others[0], onChangeOthers: handleOthersChangeFrom, icon: false, label: isOpen && i18n._({ id: 'dataGrid.filterCell.from', message: 'From' }), isValidDate: isValidDateFrom, input: false, spaceAround: true, open: true })), _jsx(DatePicker, __assign({}, rest, { timeFormat: timeFormat, value: secondDatePickerValue, initialViewDate: initialViewDate[1], secondValue: startRangeValue, onChange: setDateTo, others: others === null || others === void 0 ? void 0 : others[1], onChangeOthers: handleOthersChangeTo, icon: false, label: isOpen && i18n._({ id: 'dataGrid.filterCell.to', message: 'To' }), isValidDate: isValidDateTo, input: false, spaceAround: true, open: true, isSecondDatePicker: true }))] })) }) })] }));
};
export default DatePickerRange;
var templateObject_1;
